import { createAsyncThunk } from '@reduxjs/toolkit';
import { buildOptimizationProblem, submit_problem } from "functions/server/optimization_request.js";
import { isCartesian } from 'store/functions/coordinateSystem.js';
import { fetchTravelCosts } from 'store/reducers/thunks/costMatrixThunks.js';
import {
  countReplacedTravelCostPairs,
  costsAreAutoComputed,
  isUncostedTravelCostMatrix,
  missingTravelCostReasons,
  stillMissingMessage,
  summarizeTravelCostGaps,
  travelCostFieldsNeeded,
  UNCOSTED_MATRIX_MESSAGE,
} from 'store/functions/travelCostResults.js';
// Used at call time only; inputReducer also imports this module.
import { distanceCalculationNeeded, repairTravelCostTyping } from 'store/reducers/inputReducer';
import { travelCostTypingNeedsRepair } from 'store/functions/travelCostTyping.js';
import { generateUUID } from 'store/functions/geoJsonFunctions.js';
import { applyPinsToProblem, computePinnedAssignments } from 'store/functions/resultEditing/pinnedAssignments.js';
import get_run_status from 'functions/server/run_status.js';
import get_run_results from 'functions/server/get_run_results.js';
import cancel_run from 'functions/server/cancel_run.js';
import { UNCOSTED_TRAVEL_MATRIX_XY_MESSAGE } from 'functions/server/backendError.js';
import delete_optimization_result from 'functions/server/delete_optimization_result.js';
import updateRunName from 'functions/server/update_run_name.js';
import claim_runs from 'functions/server/claim_runs.js';
import { track, EVENTS } from 'utils/analytics.js';

// Re-own the runs submitted under an anonymous UUID to the now-signed-in account.
// Best-effort: dispatched by LoginSync on sign-in; the caller clears the pending
// id on success and leaves it to retry (next sign-in / reload) on failure.
export const claimAnonymousRuns = createAsyncThunk('posts/claimAnonymousRuns', async (payload) => {
  const anonymousUserId = payload && payload.anonymousUserId;
  if (!anonymousUserId) return { claimed: 0 };
  const response = await claim_runs(anonymousUserId);
  track(EVENTS.RUNS_CLAIMED, { claimed: response?.data?.claimed ?? 0 });
  return response;
});

// Snapshots of queued-but-not-yet-submitted problem payloads, keyed by client job
// id. Kept OUT of redux (they're large and not plain-serializable): the queue
// controller reads a snapshot when a backend slot frees, and it's dropped once the
// run is submitted or dequeued.
const pendingPayloads = new Map();

// Backoff before retrying a run the backend refused with 429 CONCURRENCY_LIMIT
// (a slot frees when some other run ends — unknowable at 429 time, so we poll).
const REQUEUE_BACKOFF_MS = 4000;

export function dropPendingPayload(id) {
  pendingPayloads.delete(id);
}

/**
 * Forget payloads for runs the job tracker no longer knows about.
 *
 * Each entry holds a fully built problem — including the O(locations²) travel
 * cost matrix — so an orphan is expensive and lives as long as the tab. Entries
 * are normally dropped on submit, failure or dequeue, but uiReducer caps
 * solveJobs at MAX_TRACKED_JOBS and slices the tail off; a run still queued
 * when it is trimmed loses its only reference and could never be dropped.
 *
 * Pruning here rather than in the reducer keeps that reducer pure and avoids an
 * import edge from uiReducer into this module. Called on enqueue, which is both
 * the only moment new entries appear and cheap: solveJobs is capped at 25.
 *
 * @param {Array<{id: string}>} solveJobs - the tracker's current jobs
 */
export function prunePendingPayloads(solveJobs) {
  if (!Array.isArray(solveJobs)) return;
  const live = new Set(solveJobs.map((job) => job && job.id).filter(Boolean));
  pendingPayloads.forEach((_value, id) => {
    if (!live.has(id)) pendingPayloads.delete(id);
  });
}

export const enqueueOptimization = createAsyncThunk('posts/enqueueOptimization', async (payload, {getState, dispatch}) => {
  /*
  Snapshots the current scenario into a problem payload and adds it to the client
  run queue. Does NOT submit — the queue controller submits it (via
  solveOptimization) once the backend has a free slot. Building the payload here
  captures the scenario as it is right now, so later edits don't change a queued run.
  Throws if the scenario can't be built (e.g. missing/invalid data).
  */

  /*
  Link the matrix to its vehicle types BEFORE anything else touches it.

  A row that names a vehicle type but isn't linked to one is invisible to the
  solver the moment any other row IS linked — the run fails outright, or drops
  the stops whose rows were skipped. Rows like that came from every scenario
  saved before 2026-09-15, from localStorage of the same age, and from every
  travel-cost upload.

  It has to run first, not just before the payload is built. Both calculations
  below merge their results onto the existing rows, and the merge key is the
  pair plus `vehicleTypeId || vehicleType` — so an unlinked row and its
  calculated replacement look like different rows, nothing matches, and a value
  someone typed is silently replaced by a computed one. It also hides the
  replacement from countReplacedTravelCostPairs, so even the x-y notice that
  exists to report exactly that could not fire.

  Checked against the plain state first, so a healthy 23k-row matrix costs one
  scan and no draft.
  */
  if (travelCostTypingNeedsRepair(getState().inputData.present)) {
    dispatch(repairTravelCostTyping());
  }

  // Cartesian scenarios submit with null coordinates, so the solver depends
  // entirely on the travel-cost matrix. Recompute it locally right before
  // snapshotting (cheap — pure arithmetic, no network) so the matrix always
  // reflects the current locations even if the user never pressed Calculate.
  if (isCartesian(getState().inputData.present)) {
    const before = getState().inputData.present.data.travel_costs;
    await dispatch(fetchTravelCosts({ silent: true })).unwrap();
    // Silent because it happens on every run, but not when it overwrote numbers
    // someone had typed or uploaded: that is a change they didn't ask for.
    const replaced = countReplacedTravelCostPairs(before, getState().inputData.present.data.travel_costs);
    if (replaced > 0) {
      dispatch({
        type: 'inputData/addNotification',
        payload: {
          notificationMsg: `Before solving we recalculated the x-y travel costs from your coordinates, which replaced ${replaced} ${replaced === 1 ? 'pair' : 'pairs'} you had typed or uploaded.`,
          notificationType: 'info',
        },
      });
    }
  }

  /*
  A geographic scenario whose travel costs were never filled in carries only the
  pair skeleton: a row per location pair with no cost, distance or duration.
  Submitted as-is, the solver "succeeds" over it and the person is shown routes
  that mean nothing; from 2026-08-29 that happened to 25 real website solves.

  Two ways get here. Nobody pressed "Populate travel costs" (the skeleton rows
  even say their values "will be auto-generated during optimization", which
  nothing did). Or they pressed it and it failed: since 2026-08-25 the backend
  refuses road-network matrices to free and signed-out users, and the refusal
  left the skeleton in place.

  So, when the cost metric is set to be calculated automatically, calculate it
  now, as the skeleton rows promise. If the matrix is still empty afterwards (the
  calculation was refused, or returned nothing), or the costs are meant to be
  entered by hand, stop with a message saying what to do rather than submit.

  Only an entirely empty matrix is stopped here. A partly filled one (a stop
  added after calculating) is submitted as before; whether those gaps should
  also be recalculated on solve is a separate decision.
  */
  if (!isCartesian(getState().inputData.present)
      && isUncostedTravelCostMatrix(getState().inputData.present.data.travel_costs)) {
    let calculatedHere = false;
    if (costsAreAutoComputed(getState().inputData.present)) {
      // Not unwrapped: a refusal is already reported by fetchTravelCosts.rejected
      // (with the upgrade prompt for a plan limit). The checks below decide. Not
      // silent: flagged rows are reported.
      // `auto`: its message says the calculation happened on the person's
      // behalf, instead of the generic success.
      await dispatch(fetchTravelCosts({ auto: true }));
      calculatedHere = true;
    }
    const afterRows = getState().inputData.present.data.travel_costs;
    if (isUncostedTravelCostMatrix(afterRows)) {
      throw new Error(UNCOSTED_MATRIX_MESSAGE);
    }
    // The calculation filled some pairs but not all. The Run buttons ask about
    // this (useTravelCostGapCheck) before ever getting here; any other caller is
    // stopped rather than solving silently over the gaps.
    if (calculatedHere) {
      const afterPresent = getState().inputData.present;
      const gaps = summarizeTravelCostGaps(afterRows, travelCostFieldsNeeded({
        costMetric: afterPresent.generalSettings.costs.costMetric,
        needDistance: distanceCalculationNeeded(afterPresent),
      }));
      if (gaps.missingPairs > 0) {
        throw new Error(stillMissingMessage(gaps.missingPairs, gaps.totalPairs, missingTravelCostReasons(gaps.missingRows)[0]));
      }
    }
  }

  const present = getState().inputData.present;
  const problem = buildOptimizationProblem(present);
  // "Re-optimize keeping my changes": stamp fixedVehicleId on the trips and
  // locations the user manually PLACED (from the amendment log, at their
  // current vehicles) so the solver preserves those assignments and repairs
  // everything else. Only when explicitly requested — a plain re-run stays a
  // clean solve.
  if (payload && payload.keepManualAssignments) {
    applyPinsToProblem(problem, computePinnedAssignments(present.data));
  }
  const id = generateUUID();
  // Scenarios loaded from the examples gallery carry their slug; sending it on
  // /problem grants the example's entitlement allowance so demo solves aren't
  // blocked by free-tier size caps (docs/backend-note-entitlements.md).
  const exampleSlug = present.generalSettings?.scenario?.exampleSlug || null;
  // Before adding one, forget any payload whose job the tracker has since
  // trimmed away — each holds a full problem including the cost matrix.
  prunePendingPayloads(getState().ui?.solveJobs);
  // `cartesian`: a rejection's message is worded for the coordinate system (solveOptimization).
  pendingPayloads.set(id, { problem, userId: present.loginData.userId, exampleSlug, cartesian: isCartesian(present) });

  const scenario = present.generalSettings && present.generalSettings.scenario;

  // Funnel: enqueuing a run is the moment of intent. `mode` separates a genuine
  // "ran their own data" activation from just solving an unedited example.
  track(EVENTS.SOLVE_STARTED, {
    modelType: present.activeModelType,
    mode: present.activeMode === 'example' ? 'example' : 'own',
  });

  return {
    id,
    scenarioName: (scenario && scenario.scenarioName) || 'Untitled scenario',
    modelType: present.activeModelType,
    // Lets the Run button stop emphasising itself for this example (uiReducer).
    exampleSlug,
  };
});

export const solveOptimization = createAsyncThunk('posts/solveOptimization', async (payload) => {
  /*
  Submits an already-queued run to the backend. Payload: { id } (the client job id).
  Solves are asynchronous: this resolves with { id, run_id, status: 'queued' } as
  soon as the backend accepts the run; the run is then polled via fetchRunStatus and
  its results loaded via fetchRunResults once it succeeds. Dispatched by the queue
  controller, not directly by the button.
  */
  const id = payload && payload.id;
  const snapshot = id && pendingPayloads.get(id);
  if (!snapshot) {
    throw new Error('Queued run payload not found');
  }
  try {
    // exampleSlug (builder-io) lets anonymous users solve gallery examples under
    // the backend's entitlements; the try/catch re-queue on 429 (main, PR #47)
    // must keep the payload for retry, so we keep that structure rather than the
    // finally-delete which would drop it.
    const response = await submit_problem(snapshot.problem, snapshot.userId, snapshot.exampleSlug);
    pendingPayloads.delete(id);
    track(EVENTS.SOLVE_SUBMITTED, { id });
    return { id, ...response };
  } catch (e) {
    // The concurrency cap is enforced per-user by the backend (across tabs), so a
    // submit can be rejected with 429 CONCURRENCY_LIMIT even when THIS tab thinks a
    // slot is free. That's not a failure — keep the payload and re-queue the run so
    // the controller retries after a short backoff, rather than surfacing an error.
    if (e && (e.code === 'CONCURRENCY_LIMIT' || e.status === 429)) {
      track(EVENTS.SOLVE_REQUEUED, { id });
      return { id, requeued: true, retryAfterMs: REQUEUE_BACKOFF_MS };
    }
    pendingPayloads.delete(id);
    // The generic wording promises the travel-cost dialog and kept values; an
    // x-y scenario gets neither (see UNCOSTED_TRAVEL_MATRIX_XY_MESSAGE).
    if (e && e.code === 'UNCOSTED_TRAVEL_MATRIX' && snapshot.cartesian) {
      e.message = UNCOSTED_TRAVEL_MATRIX_XY_MESSAGE;
    }
    // `code` (e.g. UNCOSTED_TRAVEL_MATRIX) separates a backend rejection from
    // the site's own pre-flight stop, which can carry the same wording. It is
    // an enum from our API, never user data.
    track(EVENTS.SOLVE_FAILED, { id, message: e && e.message, code: (e && e.code) || null });
    throw e;
  }
});

export const dequeueRun = createAsyncThunk('posts/dequeueRun', async (payload) => {
  /*
  Removes a run from the client queue before it's submitted. Payload: { id }.
  */
  const id = payload && payload.id;
  dropPendingPayload(id);
  return { id };
});

export const fetchRunStatus = createAsyncThunk('posts/fetchRunStatus', async (payload) => {
  /*
  Polls the lifecycle status of a queued/running solve.
  Payload: { runId }. Returns { run_id, status, error, progress, run_datetime_unix }.
  */
  const runId = payload.runId;

  if (!runId) {
    throw new Error("Missing runId input to fetchRunStatus.")
  }
  const response = await get_run_status(runId)
  return response
});

export const fetchRunResults = createAsyncThunk('posts/fetchRunResults', async (payload) => {
  /*
  Loads a finished run's results once its status is "succeeded".
  Payload: { runId }. Returns the solver's { problemResult, resultSummary }.
  */
  const runId = payload.runId;

  if (!runId) {
    throw new Error("Missing runId input to fetchRunResults.")
  }
  const response = await get_run_results(runId)
  return response
});

export const cancelRun = createAsyncThunk('posts/cancelRun', async (payload) => {
  /*
  Cancels a queued/running solve. Payload: { runId }. Returns the run's current
  status { run_id, status, ... }.
  */
  const runId = payload.runId;

  if (!runId) {
    throw new Error("Missing runId input to cancelRun.")
  }
  const response = await cancel_run(runId)
  return response
});

export const deleteOptimizationResult = createAsyncThunk('posts/deleteOptimizationResult', async (payload) => {
  const run_id = payload.run_id;

  if (!run_id) {
    throw new Error("Missing run_id input to deleteOptimizationResult.")
  }
  const response = await delete_optimization_result(run_id)
  return response
});

/*
Renames a RUN. The identifier and its action type still say "trial" — the word
the UI used to use before it settled on "run" (see ResultsSummaryListItems).
They are deliberately left alone: the action type is a persisted, analytics-
visible string, and churning it to fix a name no user ever sees would be a
bigger change than the one it tidies. Everything a person reads says run.
*/
export const renameTrial = createAsyncThunk('posts/renameTrial', async (payload) => {
  const runId = payload.runId;
  const newName = payload.newName;
  if (!runId) {
    throw new Error("Missing runId input to rename the run.")
  }
  
  const response = await updateRunName(runId, newName)
  
  return {response: response}
});
